import { useState } from "react";
import { ArrowDownUp, ExternalLink, Loader2, Plus, Route, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Chip } from "@/components/common/StatusBadge";
import { DecimalInput } from "@/components/common/DecimalInput";
import { AddressInput } from "@/components/orders/AddressInput";
import { api, errorMessage } from "@/lib/api";
import { fmtMinutes, parseDecimalInput, toInputDecimal } from "@/lib/format";
import { ROUTE_SOURCE, directionsUrl, fmtKm, hasPlace } from "@/lib/maps";
import { RouteMap, markersFromLogistics } from "@/components/maps/RouteMap";
import { cn } from "@/lib/utils";

const NONE = "__ingen__";
const LETTERS = "ABCDEFGHIJ";

const Marker = ({ children, tone = "neutral" }) => (
  <span className={cn("num flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold", tone === "primary" ? "bg-primary text-primary-foreground" : tone === "dark" ? "bg-brand-charcoal text-white" : "bg-secondary text-foreground")} aria-hidden="true">
    {children}
  </span>
);

const waypoint = (p) => ({ place_id: p?.place_id || null, lat: p?.lat ?? null, lng: p?.lng ?? null, address: p?.address || p?.name || null });

/**
 * Stader og rute: hentestad → stopp → leveringsstad, bil, planlagt tid, avstand/køyretid (Google eller estimat) og tilkomst.
 * value = logistics-objektet på ordra. onChange(partial) slår saman.
 */
export const RoutePlanner = ({ value, onChange, locations = [], vehicles = [], mapsAvailable = true, testId = "route-planner" }) => {
  const lg = value || {};
  const stops = lg.stops || [];
  const [routeState, setRouteState] = useState({ loading: false, error: null, warning: null, legs: null });

  const set = (patch) => onChange({ ...lg, ...patch });
  // Når stadene endrar seg er ei tidlegare rekna rute ugyldig – nullstill (manuelt sette tal blir verande).
  const setPlaces = (patch) => {
    const computed = ["google", "osrm", "estimat"].includes(lg.distance_source);
    set({ ...patch, route_geometry: null, ...(computed ? { distance_km: null, duration_min: null, distance_source: null } : {}) });
  };
  const setStop = (i, place) => setPlaces({ stops: stops.map((s, idx) => (idx === i ? place : s)) });
  const removeStop = (i) => setPlaces({ stops: stops.filter((_, idx) => idx !== i) });
  const swap = () => setPlaces({ pickup: lg.delivery || null, delivery: lg.pickup || null });

  const canRoute = hasPlace(lg.pickup) && hasPlace(lg.delivery);
  const mapsLink = directionsUrl({ origin: lg.pickup, destination: lg.delivery, stops });

  const computeRoute = async () => {
    if (!canRoute) return;
    setRouteState({ loading: true, error: null, warning: null, legs: null });
    try {
      const res = await api.post("/maps/route", { origin: waypoint(lg.pickup), destination: waypoint(lg.delivery), intermediates: stops.filter(hasPlace).map(waypoint) });
      const d = res.data;
      // Fyll inn koordinatar frå ruteoppslaget på stader som mangla dei (t.d. manuelt skrivne adresser).
      const wps = d.waypoints || [];
      const withCoords = (p, wp) => (p && wp && (p.lat === null || p.lat === undefined) ? { ...p, lat: wp.lat, lng: wp.lng } : p);
      const validStops = stops.filter(hasPlace);
      set({
        pickup: withCoords(lg.pickup, wps[0]),
        stops: stops.map((st) => (hasPlace(st) ? withCoords(st, wps[1 + validStops.indexOf(st)]) : st)),
        delivery: withCoords(lg.delivery, wps[wps.length - 1]),
        distance_km: d.distance_km,
        duration_min: d.duration_min,
        distance_source: ["google", "osrm"].includes(d.source) ? d.source : "estimat",
        route_geometry: d.geometry && d.geometry.length > 1 ? d.geometry : null,
      });
      setRouteState({ loading: false, error: null, warning: d.warning || null, legs: d.legs || null });
    } catch (err) {
      setRouteState({ loading: false, error: errorMessage(err, "Kunne ikkje rekne ut ruta."), warning: null, legs: null });
    }
  };

  const manual = (key, text) => {
    const n = parseDecimalInput(text);
    set({ [key]: text === "" ? null : Number.isNaN(n) ? lg[key] : n, distance_source: "manuell" });
  };

  const source = lg.distance_source ? ROUTE_SOURCE[lg.distance_source] : null;
  const hasDistance = lg.distance_km !== null && lg.distance_km !== undefined && lg.distance_km !== "";

  return (
    <div className="space-y-5" data-testid={testId}>
      <div className="space-y-2.5">
        <div className="flex items-center gap-3">
          <Marker tone="dark">A</Marker>
          <div className="min-w-0 flex-1">
            <AddressInput value={lg.pickup || null} onChange={(p) => setPlaces({ pickup: p })} placeholder="Hentestad (steinbrot, lager, kunde …)" locations={locations} mapsAvailable={mapsAvailable} testId={`${testId}-pickup`} />
          </div>
          <Button type="button" variant="ghost" size="icon" className="h-10 w-10 shrink-0 text-muted-foreground" onClick={swap} aria-label="Byt om hente- og leveringsstad" data-testid={`${testId}-swap`}>
            <ArrowDownUp className="h-4 w-4" />
          </Button>
        </div>
        {stops.map((stop, i) => (
          <div key={i} className="flex items-center gap-3">
            <Marker>{LETTERS[i + 1]}</Marker>
            <div className="min-w-0 flex-1">
              <AddressInput value={stop} onChange={(p) => setStop(i, p)} placeholder={`Stopp ${i + 1}`} locations={locations} mapsAvailable={mapsAvailable} testId={`${testId}-stop-${i}`} />
            </div>
            <Button type="button" variant="ghost" size="icon" className="h-10 w-10 shrink-0 text-muted-foreground hover:text-destructive" onClick={() => removeStop(i)} aria-label="Fjern stopp" data-testid={`${testId}-remove-stop-${i}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <div className="flex items-center gap-3">
          <Marker tone="primary">{LETTERS[stops.length + 1]}</Marker>
          <div className="min-w-0 flex-1">
            <AddressInput value={lg.delivery || null} onChange={(p) => setPlaces({ delivery: p })} placeholder="Leverings-/arbeidsstad" locations={locations} mapsAvailable={mapsAvailable} testId={`${testId}-delivery`} />
          </div>
          <span className="h-10 w-10 shrink-0" aria-hidden="true" />
        </div>
        <div className="pl-10">
          <Button type="button" variant="ghost" size="sm" className="h-9 px-2 text-muted-foreground hover:text-foreground" onClick={() => set({ stops: [...stops, null] })} disabled={stops.length >= 8} data-testid={`${testId}-add-stop`}>
            <Plus className="mr-1.5 h-4 w-4" /> Legg til stopp
          </Button>
        </div>
      </div>

      <RouteMap markers={markersFromLogistics(lg)} geometry={lg.route_geometry} source={lg.distance_source} heightClassName="h-52 md:h-64" emptyText="Kartet viser stadene etter kvart som du vel adresser." testId={`${testId}-map`} />

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Bil / køyretøy</Label>
          <Select value={lg.vehicle_resource_id || NONE} onValueChange={(v) => set({ vehicle_resource_id: v === NONE ? null : v })}>
            <SelectTrigger className="md:h-10" data-testid={`${testId}-vehicle`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover">
              <SelectItem value={NONE}>Ikkje vald</SelectItem>
              {vehicles.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {r.name}{r.payload_kg ? ` · ${(r.payload_kg / 1000).toLocaleString("nb-NO")} t` : ""}{r.registration ? ` · ${r.registration}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${testId}-time`}>Planlagt klokkeslett</Label>
          <Input id={`${testId}-time`} type="time" value={lg.planned_time || ""} onChange={(e) => set({ planned_time: e.target.value || null })} className="tabular h-11 bg-card md:h-10" data-testid={`${testId}-time`} />
        </div>
      </div>

      <div className="rounded-lg border border-border bg-background" data-testid={`${testId}-summary`}>
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <Route className="h-4 w-4 text-muted-foreground" />
            <p className="text-[13px] font-semibold">Avstand og køyretid</p>
            {source ? <Chip tone={source.tone} outline data-testid={`${testId}-source`}>{source.label}</Chip> : null}
          </div>
          <div className="flex items-center gap-2">
            {mapsLink ? (
              <Button type="button" variant="ghost" size="sm" className="h-9 px-2 text-muted-foreground hover:text-foreground" asChild>
                <a href={mapsLink} target="_blank" rel="noopener noreferrer" data-testid={`${testId}-maps-link`}><ExternalLink className="mr-1.5 h-4 w-4" /> Opne i Google Maps</a>
              </Button>
            ) : null}
            <Button type="button" variant="outline" size="sm" className="h-9 bg-card" onClick={computeRoute} disabled={!canRoute || routeState.loading || !mapsAvailable} data-testid={`${testId}-compute`}>
              {routeState.loading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              {hasDistance ? "Rekn ut på nytt" : "Rekn ut rute"}
            </Button>
          </div>
        </div>
        <div className="grid gap-3 border-t border-border px-4 py-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
          <div className="space-y-1.5">
            <Label htmlFor={`${testId}-km`} className="text-xs text-muted-foreground">Avstand éin veg (km)</Label>
            <DecimalInput id={`${testId}-km`} value={toInputDecimal(lg.distance_km)} onChange={(t) => manual("distance_km", t)} placeholder="t.d. 24,5" className="bg-card md:h-10" data-testid={`${testId}-km`} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${testId}-min`} className="text-xs text-muted-foreground">Køyretid éin veg (min)</Label>
            <DecimalInput id={`${testId}-min`} value={toInputDecimal(lg.duration_min)} onChange={(t) => manual("duration_min", t)} placeholder="t.d. 35" className="bg-card md:h-10" data-testid={`${testId}-min`} />
          </div>
          <div className="text-sm text-muted-foreground sm:pb-2.5">
            {hasDistance ? <span className="tabular">Tur/retur {fmtKm(lg.distance_km * 2)} · {fmtMinutes(Math.round((lg.duration_min || 0) * 2))}</span> : <span>Ikkje rekna ut enno</span>}
          </div>
        </div>
        {routeState.legs && routeState.legs.length > 1 ? (
          <p className="tabular border-t border-border px-4 py-2 text-xs text-muted-foreground">Etappar: {routeState.legs.map((l, i) => `${LETTERS[i]}→${LETTERS[i + 1]} ${fmtKm(l.distance_km)}`).join(" · ")}</p>
        ) : null}
        {routeState.warning || !mapsAvailable || routeState.error ? (
          <p className={cn("border-t border-border px-4 py-2 text-xs leading-5", routeState.error ? "text-destructive" : "text-muted-foreground")} data-testid={`${testId}-warning`}>
            {routeState.error || (!mapsAvailable ? "Kartteneste er ikkje konfigurert – skriv inn avstand og køyretid manuelt." : routeState.warning)}
          </p>
        ) : null}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${testId}-access`}>Tilkomst og merknad til sjåfør</Label>
        <Textarea id={`${testId}-access`} value={lg.access_note || ""} onChange={(e) => set({ access_note: e.target.value || null })} rows={2} placeholder="t.d. Smal veg siste 400 m, ring kunden 20 min før. Tipp ved innkøyrsla." className="bg-card" data-testid={`${testId}-access`} />
      </div>
    </div>
  );
};
